import { useEffect } from 'react'

import { useGrowthBook } from '@growthbook/growthbook-react'

import {
  getIsNewUserForManualMatchingV2Experiment,
  getUserId,
} from 'src/Redux/auth-process/userSlice/selectors'
import {
  useUpdateArtistFeatureFlagsMutation,
  useGetArtistFeatureFlagsQuery,
} from 'src/Redux/A-BFeaturesApi'
import { useAppSelector } from 'src/Hooks/redux'
import {
  usePaidCampaignsCountQuery,
  getHasPaidCampaign,
} from 'src/Redux/metadataApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { APP } from 'src/Configs/App'

export const MANUAL_MATCHING_V2_GB_FEATURE =
  'spotify_manual_matching_min_budget_v2'
export const MANUAL_MATCHING_V2_BE_FEATURE = 'new_artist_manual_matching_v2'
const CONTROL_VARIATION = 0
const TEST_VARIATION = 1

export const useIsManualMatchingV2Enabled = (): boolean => {
  const { data, isSuccess } = useGetArtistFeatureFlagsQuery(void 0, {
    selectFromResult: (result) => ({
      ...result,
      data: result?.data?.features?.find(
        (item) => item?.name === MANUAL_MATCHING_V2_BE_FEATURE,
      )?.variation,
    }),
  })

  return APP.MANUAL_MATCHING_ENABLED && isSuccess && data === TEST_VARIATION
}

export const useManualMatchingV2Experiment = (): null => {
  const growthbook = useGrowthBook()
  const isGrowthBookReady = growthbook.ready
  const growthbookAttributes = growthbook.getAttributes()?.id
  const userId = useAppSelector(getUserId)
  const isRegisteredAfterCutoff = useAppSelector(
    getIsNewUserForManualMatchingV2Experiment,
  )
  const { data: spotifyPaidData, isSuccess: isPaidDataLoaded } =
    usePaidCampaignsCountQuery(CampaignPlatformTypeEnum.SPOTIFY)
  const isNewUser =
    isRegisteredAfterCutoff && !getHasPaidCampaign(spotifyPaidData)

  const [addFeatureForUser] = useUpdateArtistFeatureFlagsMutation()

  const { data: hasManualMatchingV2Feature, isSuccess: featuresIsSucceeded } =
    useGetArtistFeatureFlagsQuery(void 0, {
      selectFromResult: (result) => ({
        ...result,
        data: result?.data?.features?.some(
          (item) => item?.name === MANUAL_MATCHING_V2_BE_FEATURE,
        ),
      }),
    })

  const isDataLoaded =
    featuresIsSucceeded && isPaidDataLoaded && isGrowthBookReady

  useEffect(() => {
    if (!APP.MANUAL_MATCHING_ENABLED) {
      return
    }

    const isFeatureNeeded =
      isDataLoaded && !hasManualMatchingV2Feature && isNewUser

    const isAttributesSet = Boolean(userId) && growthbookAttributes === userId

    if (isFeatureNeeded && isAttributesSet) {
      const variant = growthbook.getFeatureValue(
        MANUAL_MATCHING_V2_GB_FEATURE,
        CONTROL_VARIATION,
      )

      addFeatureForUser({
        featureName: MANUAL_MATCHING_V2_BE_FEATURE,
        variation: Number(variant),
      })
    }
  }, [
    addFeatureForUser,
    hasManualMatchingV2Feature,
    growthbook,
    isDataLoaded,
    isNewUser,
    growthbookAttributes,
    userId,
  ])

  return null
}
